import {
  ArrowRight,
  Briefcase,
  ChevronDown,
  FileCheck,
  Lock,
  Mic,
  Moon,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  Sun,
  UserCheck,
  Globe,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useState, type ReactNode } from "react"

import { SignInCard, type SignInMode } from "@/components/auth/sign-in-card"
import { Demo } from "@/components/landing/demo"
import { WhyPraxa } from "@/components/landing/why-praxa"
import { AmbientBackdrop } from "@/components/layout/hero"
import { Button } from "@/components/ui/button"
import { useTheme } from "@/hooks/use-theme"

/**
 * The only page anyone sees before signing in - so it is also the page that has
 * to say what PRAXA is.
 *
 * It scrolls: the sign-in box is in the first screen for people coming back,
 * and everything below it is for people deciding whether to register - what
 * it does, a demo, how it starts, why it can be trusted, and the questions
 * people ask before they hand over a CV.
 *
 * Every claim here has to be true of the deployed app. Two of them - "only you
 * see your data" and the daily limit - are delivered by phase 1 step 3
 * (docs/weaknesses.md, W25), and the app is not deployed before that.
 */
export function SignInPage() {
  const [mode, setMode] = useState<SignInMode>("sign-in")

  function goToCard(next: SignInMode) {
    setMode(next)
    document.getElementById("sign-in")?.scrollIntoView({ behavior: "smooth", block: "center" })
    window.setTimeout(() => document.getElementById("email")?.focus({ preventScroll: true }), 400)
  }

  return (
    <div className="relative min-h-dvh">
      <AmbientBackdrop />
      <TopBar onSignIn={() => goToCard("sign-in")} />

      <main>
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 pb-16 pt-10 lg:grid-cols-[1.25fr_1fr] lg:pt-16">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur-sm">
              <Sparkles className="size-3 text-primary" aria-hidden />
              Career copilot
            </p>
            <h1 className="mt-5 font-display text-5xl font-extrabold tracking-[-0.045em] sm:text-6xl">
              Find the right job.
              <br />
              <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent">
                Apply honestly.
              </span>
              <br />
              Walk in prepared.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Most job tools help you find postings. PRAXA helps your application get past the
              screening software and in front of a person: it scores each posting against your real
              CV, tailors your CV and letter in the posting&apos;s own language without inventing a
              single fact, and prepares you for the interview that follows.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => goToCard("register")}>
                Create a free account
                <ArrowRight aria-hidden />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() =>
                  document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <PlayCircle aria-hidden />
                Watch the demo
              </Button>
            </div>
            <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <TrustTick>Never invents a fact</TrustTick>
              <TrustTick>You approve every document</TrustTick>
              <TrustTick>Public job sources only</TrustTick>
            </ul>
          </div>

          <div id="sign-in" className="mx-auto w-full max-w-sm scroll-mt-24">
            <SignInCard mode={mode} onModeChange={setMode} />
          </div>
        </section>

        {/* The whole product in one picture, before any of the detail below it.
            A static SVG in `public/art/`: drawn, not generated, so it is sharp
            at any width, costs nothing to serve and needs no network call.
            The same file is the README's cover image. */}
        <section className="mx-auto max-w-6xl px-6 pb-16" aria-label="The PRAXA journey">
          <img
            src="/art/praxa-journey.svg"
            alt="The PRAXA journey: an engineer goes from finding and scoring jobs, to a CV and letter checked claim by claim, to practising the interview, and on to the offer."
            width={1200}
            height={560}
            decoding="async"
            className="h-auto w-full rounded-[2rem] border border-border/60 shadow-xl shadow-primary/10"
          />
        </section>

        <WhyPraxa />

        <Section
          id="features"
          eyebrow="What it does"
          title="Three jobs, one place"
          description="The work between finding a posting and walking out of the interview."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <FeatureCard
              icon={Briefcase}
              title="Find the jobs worth applying for"
              body="Fetches postings from public job boards and scores each one against your CV: what matches, what is missing, and whether to apply."
            />
            <FeatureCard
              icon={FileCheck}
              title="Tailor your CV and letter"
              body="A writing agent re-orders and re-words what your CV already says for one posting. A second agent checks every claim. You approve before anything is saved."
            />
            <FeatureCard
              icon={Mic}
              title="Practise the interview"
              body="Questions built from the posting, one at a time. Each answer is scored on structure, specificity, ownership, impact and relevance, with one thing to do better."
            />
          </div>
        </Section>

        <Section
          id="demo"
          eyebrow="Demo"
          title="See it work"
          description="A short walk through each job with an invented candidate. Pick a tab, or press Replay."
        >
          <Demo />
        </Section>

        <Section id="how" eyebrow="Getting started" title="Three steps to your first match">
          <ol className="grid gap-4 md:grid-cols-3">
            <StepCard
              n={1}
              title="Create your account"
              body="Register with your e-mail and open the confirmation link. That is the whole sign-up."
            />
            <StepCard
              n={2}
              title="Add your CV"
              body="Upload the CV you actually send. PRAXA only ever works from what is in it."
            />
            <StepCard
              n={3}
              title="Find, write, practise"
              body="Fetch postings, score them, tailor the documents for the best ones, then rehearse the interview."
            />
          </ol>
        </Section>

        <Section id="trust" eyebrow="Why trust it" title="Built to be checked, not believed">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TrustCard
              icon={ShieldCheck}
              title="No invented facts"
              body="A missing number stays a marked gap. The reviewing agent flags anything your CV does not support."
            />
            <TrustCard
              icon={UserCheck}
              title="You decide"
              body="Drafts are shown first. Approve saves them; Discard leaves nothing behind."
            />
            <TrustCard
              icon={Lock}
              title="Your data is yours"
              body="Your CVs, jobs and interviews belong to your account alone. The database itself refuses them to anyone else."
            />
            <TrustCard
              icon={Globe}
              title="Fair sources"
              body="Only job boards that allow automated access. Sites that forbid it - LinkedIn, Indeed, StepStone - are never touched."
            />
          </div>
        </Section>

        <Section id="faq" eyebrow="Questions" title="Before you sign up">
          <div className="mx-auto max-w-3xl divide-y rounded-2xl border bg-card/60 backdrop-blur">
            <Faq q="What does it cost?">
              Nothing. Sign up and use it every day: each account has a daily allowance of AI
              use - around 25 questions, or a few tailored CVs and letters - that starts again
              every day. The limit is what keeps PRAXA free and fair for everyone.
            </Faq>
            <Faq q="Who can see my CV?">
              Only you. Every record is stored against your account, and the database checks the
              owner on every read - not just the app.
            </Faq>
            <Faq q="Will it make things up to get me the job?">
              No. It can re-order, re-word and emphasise what your CV says, but it adds no
              experience, tools or numbers. A second agent checks the draft against your CV and
              lists anything it cannot find there.
            </Faq>
            <Faq q="Will it get my CV past every applicant tracking system?">
              No tool can promise that - every company configures its system differently, and some
              screening is done by people. What PRAXA does is remove the avoidable reasons: wording
              that does not match the posting even though your experience does, and file layouts
              that parsers are known to misread. It also tells you honestly when a posting asks for
              something your CV does not show.
            </Faq>
            <Faq q="Where do the jobs come from?">
              Arbeitnow, Remotive and the Bundesagentur für Arbeit, through the official public
              interfaces they offer for exactly this - and any posting you paste in yourself.
              PRAXA never copies websites, and sites that forbid automated access, such as
              LinkedIn, Indeed and StepStone, are never touched.
            </Faq>
            <Faq q="Does it work in German?">
              Yes. Ask in English or German; it searches postings and guides in both languages.
            </Faq>
            <Faq q="What does it remember about me?">
              Lasting preferences you state - "no relocation", "remote only" - so it does not ask
              again. You can see and delete every one of them on its own page.
            </Faq>
          </div>
        </Section>

        <section className="mx-auto max-w-6xl px-6 pb-20">
          <div className="flex flex-col items-center gap-4 rounded-2xl border bg-gradient-to-br from-primary/10 via-card/60 to-accent/10 px-6 py-10 text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight">Ready for your next role?</h2>
            <p className="max-w-lg text-muted-foreground">
              Create an account in a minute and score your first posting today.
            </p>
            <Button size="lg" onClick={() => goToCard("register")}>
              Create a free account
              <ArrowRight aria-hidden />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

function TopBar({ onSignIn }: { onSignIn: () => void }) {
  const { theme, toggle } = useTheme()
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-6 px-6">
        <button
          type="button"
          className="flex items-baseline gap-2"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <span className="font-display text-lg font-bold tracking-tight">PRAXA</span>
          <span className="hidden font-mono text-[10px] uppercase tracking-widest text-muted-foreground sm:inline">
            Career copilot
          </span>
        </button>
        <nav className="hidden flex-1 items-center gap-5 text-sm text-muted-foreground md:flex" aria-label="Page sections">
          <a href="#why" className="hover:text-foreground">Why PRAXA</a>
          <a href="#features" className="hover:text-foreground">What it does</a>
          <a href="#demo" className="hover:text-foreground">Demo</a>
          <a href="#how" className="hover:text-foreground">Getting started</a>
          <a href="#faq" className="hover:text-foreground">Questions</a>
          <a href="/how-it-works" className="hover:text-foreground">How it works</a>
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {theme === "dark" ? <Sun aria-hidden /> : <Moon aria-hidden />}
          </Button>
          <Button size="sm" variant="outline" onClick={onSignIn}>
            Sign in
          </Button>
        </div>
      </div>
    </header>
  )
}

function Section({
  id,
  eyebrow,
  title,
  description,
  children,
}: {
  id: string
  eyebrow: string
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section id={id} className="mx-auto max-w-6xl scroll-mt-20 px-6 pb-20">
      <div className="mb-8 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">{title}</h2>
        {description ? (
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function TrustTick({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      <ShieldCheck className="size-4 text-success" aria-hidden />
      {children}
    </li>
  )
}

function FeatureCard({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-2xl border bg-card/60 p-6 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/5">
      <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="size-5" aria-hidden />
      </span>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}

function StepCard({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="rounded-2xl border bg-card/60 p-6 backdrop-blur">
      <span className="font-mono text-sm text-primary">0{n}</span>
      <h3 className="mt-2 font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </li>
  )
}

function TrustCard({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-2xl border bg-card/60 p-5 backdrop-blur">
      <Icon className="size-5 text-success" aria-hidden />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
        {q}
        <ChevronDown
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{children}</p>
    </details>
  )
}

function Footer() {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-6 py-8 text-xs text-muted-foreground sm:flex-row sm:justify-between">
        <p>© 2026 PRAXA</p>
        <p>
          Job data from{" "}
          <a className="underline hover:text-foreground" href="https://www.arbeitnow.com" target="_blank" rel="noreferrer">
            Arbeitnow
          </a>
          ,{" "}
          <a className="underline hover:text-foreground" href="https://remotive.com" target="_blank" rel="noreferrer">
            Remotive
          </a>{" "}
          and the{" "}
          <a className="underline hover:text-foreground" href="https://www.arbeitsagentur.de" target="_blank" rel="noreferrer">
            Bundesagentur für Arbeit
          </a>
          .
        </p>
      </div>
    </footer>
  )
}
